import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { EventManagerService } from './event-manager.service';
import { AppLoggerService } from './app-logger.service';
import { defaultConfig } from 'src/app/app.config';
import { wasLeftBySessionEnd } from '../utils/session-leave';

const { sessionClosedByServer } = defaultConfig.appEvents;

/**
 * Bug 281127 — al «Cerrar sesión» la app limpia los tokens y redirige a Hydra; el navegador guarda
 * la página en el back-forward cache y, al volver atrás, la restaura tal cual (DOM y estado JS) sin
 * recargar ni pasar por `authGuard`: se veía el dashboard sin tokens ni sesión y la primera petición
 * al gateway terminaba en SESSION_CLOSED. `pageshow` con `persisted` delata esa restauración; sin
 * tokens se trata como sesión cerrada.
 *
 * Reapertura (6-oct): el aviso se pintaba ENCIMA de la página restaurada, así que la vista de la
 * app seguía a la vista. Ahora la página que se dejó al cerrar sesión llega oculta
 * (`hideAppBeforeLeaving`) y se recarga desde la raíz: la carga nueva pasa por los guards y termina en
 * el login, sin mostrar nada. Las pantallas públicas (`/auth/...`) no se tocan: sin sesión es su
 * estado normal y el aviso sobraba.
 */
@Injectable({ providedIn: 'root' })
export class RestoredPageSessionService {
  private authService = inject(AuthService);
  private eventManager = inject(EventManagerService);
  private logger = inject(AppLoggerService);
  private started = false;

  start(target: Pick<Window, 'addEventListener'> = window): void {
    if (this.started) {
      return;
    }
    this.started = true;
    target.addEventListener('pageshow', (event: Event) => this.onPageShow(event as PageTransitionEvent));
  }

  onPageShow(
    event: Pick<PageTransitionEvent, 'persisted'>,
    location: Pick<Location, 'replace' | 'pathname'> = window.location
  ): void {
    if (!event?.persisted || this.authService.isAuthenticated()) {
      return;
    }
    if (wasLeftBySessionEnd()) {
      this.logger.warn('[RestoredPageSession] Página dejada al cerrar sesión restaurada del bfcache: se recarga');
      location.replace(document.baseURI);
      return;
    }
    if (/\/auth(\/|$)/.test(location.pathname)) {
      return;
    }
    this.logger.warn('[RestoredPageSession] Página restaurada del bfcache sin sesión: se cierra');
    this.eventManager.next(sessionClosedByServer.name, null);
  }
}
